import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Timer, Warning, CheckCircle, ArrowRight } from "@phosphor-icons/react";

const styleFor = (u) => ({
  overdue:  { border: "border-red-700 bg-red-100",   text: "text-red-800",   label: "OVERDUE" },
  critical: { border: "border-red-500 bg-red-50",    text: "text-red-700",   label: "CRITICAL" },
  warn:     { border: "border-amber-500 bg-amber-50",text: "text-amber-700", label: "SOON" },
  ok:       { border: "border-emerald-500 bg-emerald-50", text: "text-emerald-700", label: "ON TRACK" },
}[u] || { border: "border-slate-300 bg-white", text: "text-slate-600", label: "—" });

/**
 * PA law: 30 days to return security deposits with itemization. Displays a
 * live countdown for every recent move-out. Slots into Admin dashboard.
 */
export default function DepositCountdownWidget() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/deposit-countdown").then(r => setData(r.data)).catch(() => setData({ rows: [] }));
  }, []);

  if (!data || !data.rows || data.rows.length === 0) return null;

  return (
    <div className="mt-4 border-4 border-slate-900 bg-white shadow-[6px_6px_0_#0f172a]" data-testid="deposit-countdown">
      <div className="p-3 bg-slate-900 text-white flex items-center justify-between">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-amber-300">PA · 30-Day Deposit Law</div>
          <div className="font-display font-black text-lg tracking-tight flex items-center gap-2">
            <Timer weight="fill" size={18}/> Deposit Countdown · {data.count} move-out{data.count === 1 ? "" : "s"}
          </div>
        </div>
        {(data.overdue > 0 || data.critical > 0) && (
          <div className="text-right">
            {data.overdue > 0 && <div className="text-red-400 text-xs font-black uppercase tracking-widest">🚨 {data.overdue} overdue</div>}
            {data.critical > 0 && <div className="text-amber-300 text-xs font-black uppercase tracking-widest">⏰ {data.critical} critical</div>}
          </div>
        )}
      </div>
      <div className="divide-y divide-slate-100">
        {data.rows.slice(0, 8).map((r, i) => {
          const s = styleFor(r.urgency);
          return (
            <Link key={i} to={`/admin/properties/${encodeURIComponent(r.address)}/unit/${encodeURIComponent(r.unit)}`}
              data-testid={`deposit-row-${i}`}
              className={`flex items-center gap-3 p-2 border-l-4 ${s.border} hover:bg-slate-50`}>
              <div className={`w-16 text-center flex-shrink-0`}>
                <div className={`font-display font-black text-2xl leading-none ${s.text} tabular-nums`}>
                  {r.days_left < 0 ? `+${-r.days_left}` : r.days_left}
                </div>
                <div className={`text-[9px] font-black uppercase tracking-widest ${s.text}`}>{s.label}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-sm truncate">{r.address} {r.unit && `· ${r.unit}`}</div>
                <div className="text-[10px] text-slate-500 truncate">
                  {r.tenant_name && <>{r.tenant_name} · </>}
                  Moved out {r.move_out_date}
                  {r.letter_sent && <span className="text-emerald-700 font-black uppercase tracking-widest ml-1"> ✓ letter sent</span>}
                </div>
              </div>
              <ArrowRight weight="bold" size={14} className="text-slate-400"/>
            </Link>
          );
        })}
      </div>
      {data.rows.length > 8 && (
        <div className="p-2 text-center text-[10px] text-slate-500 font-black uppercase tracking-widest">
          +{data.rows.length - 8} more
        </div>
      )}
    </div>
  );
}
